import type { ScanAgentApi, ScanEnding, ScanOutcomeCode, ScannedPage } from './scan-agent';
import { wordsForOutcome } from './scan-outcomes';
import { trackProduct } from './product-tracker';

/** How a scan ended: the pages it handed over, the message to show when it ended badly, and why. */
export interface ScanOutcome {
  pages: number;
  /** Blank sides Paige Scan left out. */
  blank: number;
  problem: string | null;
  /** The kind of failure for the product event, never the scanner's message. */
  failed: 'no_pages' | 'scanner' | null;
  /** The agent's outcome code (Paige Scan 1.5.2 and later). */
  code: ScanOutcomeCode | null;
  layer?: string | undefined;
  detail?: string | undefined;
}

/** How many times in a row the agent may fail to answer a poll before the scan is given up on. */
export const POLL_RETRIES = 3;

/**
 * Polls for pages until the scan ends. One poll that does not get through (the agent busy turning a
 * big page into a JPEG, a blip) is asked again; only several in a row end the scan, and the pages
 * already here stay.
 */
export async function collectPages(
  api: ScanAgentApi,
  id: string,
  onPage: (page: ScannedPage) => void,
): Promise<ScanOutcome> {
  let pages = 0;
  let misses = 0;
  for (;;) {
    let poll;
    try {
      poll = await api.next(id);
      misses = 0;
    } catch (error) {
      if (++misses >= POLL_RETRIES) throw error;
      continue;
    }
    if (poll.status === 'page') {
      pages += 1;
      onPage(poll.page);
    } else if (poll.status !== 'waiting') {
      return ending(poll, pages);
    }
  }
}

/** A scan that finished: nothing to say unless the feeder gave nothing at all. */
function done(base: Omit<ScanOutcome, 'problem' | 'failed'>): ScanOutcome {
  if (base.pages === 0 && base.blank === 0)
    return {
      ...base,
      problem: 'The scanner returned no pages; check that paper is loaded.',
      failed: 'no_pages',
    };
  return { ...base, problem: null, failed: null };
}

/** The ending's words: the agent's outcome code when it sends one, its sentence otherwise. */
export function ending(
  poll: ScanEnding & { status: string; reason?: string },
  pages: number,
): ScanOutcome {
  const base = {
    pages,
    blank: poll.blank ?? 0,
    code: poll.outcome ?? null,
    layer: poll.layer,
    detail: poll.detail,
  };
  if (poll.status === 'done') return done(base);
  if (poll.status === 'cancelled') return { ...base, problem: null, failed: null };
  const words = wordsForOutcome(poll.outcome, pages);
  return {
    ...base,
    problem: words?.title ?? poll.reason ?? 'The scanner could not finish.',
    failed: poll.outcome === 'noPaper' ? 'no_pages' : 'scanner',
  };
}

/** scan.finished with the page count, or scan.failed with the kind of failure. */
export function trackOutcome(outcome: ScanOutcome): void {
  if (outcome.failed)
    trackProduct('scan.failed', {
      reason: outcome.failed,
      ...(outcome.code ? { outcome: outcome.code } : {}),
    });
  else trackProduct('scan.finished', { pages: outcome.pages, blank: outcome.blank });
}
